import { createContext, useContext, useEffect, useState } from "react";
import { getAgentAccess, getAgentAccessSalesLeader } from "../api/agent-access";
import { getSalesLeaderAgentProfile } from "../api/internal-identity";
import { UserContext } from "./UserContextProvider";
import { ClientConfigurationContext } from "./ClientConfigurationContextProvider";
import Loader, { Types } from "../components/gdk/Loader";

export const AgentAccessContext = createContext();

function salesLeaderProfileToSubstituteAgentAccessBody(profile) {
    if (!profile) {
        return null;
    }
    return {
        AgentId: profile.agentId ?? null,
        AgencyId: profile.agencyId ?? null,
    };
}

function substituteAgentAccessBodyFromLocalStorage() {
    return {
        AgentId: localStorage.getItem("substituteAgentId") || null,
        AgencyId: localStorage.getItem("substituteAgencyId") || null,
    };
}

async function fetchSubstituteInfoForViewReports(clientConfiguration) {
    if (!clientConfiguration.enableSalesLeaderSessionFeature) {
        return substituteAgentAccessBodyFromLocalStorage();
    }
    try {
        const profile = await getSalesLeaderAgentProfile();
        return salesLeaderProfileToSubstituteAgentAccessBody(profile);
    } catch {
        return null;
    }
}

function runDashboardAgentAccess(cancelled, setAgentAccess, setSalesLeader) {
    getAgentAccessSalesLeader((response) => {
        if (!cancelled) {
            setAgentAccess(response);
            setSalesLeader(true);
        }
    });
}

async function runViewReportsAgentAccess(clientConfiguration, cancelled, setAgentAccess) {
    const substituteAgentInfo = await fetchSubstituteInfoForViewReports(clientConfiguration);
    if (cancelled) {
        return;
    }
    getAgentAccessSalesLeader((response) => {
        if (!cancelled) {
            setAgentAccess(response);
        }
    }, substituteAgentInfo);
}

function runDefaultAgentAccess(cancelled, setAgentAccess) {
    getAgentAccess((response) => {
        if (!cancelled) {
            setAgentAccess(response);
        }
    });
}

function runAgentAccessByRoute(pathname, clientConfiguration, cancelled, setAgentAccess, setSalesLeader) {
    if (pathname === "/dashboard") {
        runDashboardAgentAccess(cancelled, setAgentAccess, setSalesLeader);
        return;
    }
    if (pathname === "/view-reports") {
        return runViewReportsAgentAccess(clientConfiguration, cancelled, setAgentAccess);
    }
    runDefaultAgentAccess(cancelled, setAgentAccess);
    return Promise.resolve();
}

export function AgentAccessContextProvider({ children }) {
    const user = useContext(UserContext);
    const clientConfiguration = useContext(ClientConfigurationContext);
    const [agentAccess, setAgentAccess] = useState();
    const [salesLeader, setSalesLeader] = useState(false);

    useEffect(() => {
        if (!user || user.loginRequired || !clientConfiguration) {
            return undefined;
        }

        let cancelled = false;
        const pathname = window.location.pathname;

        const run = () =>
            runAgentAccessByRoute(
                pathname,
                clientConfiguration,
                cancelled,
                setAgentAccess,
                setSalesLeader
            );

        void run();

        return () => {
            cancelled = true;
        };
    }, [user, clientConfiguration]);

    return <AgentAccessContext.Provider value={agentAccess}>
        {user && !user.loginRequired &&
            !agentAccess && !salesLeader
            ? <Loader type={Types.Section} />
            : <>{children}</>
        }
    </AgentAccessContext.Provider>;
}
